import dayjs from "apps/visits/packages/shared/libs/dayjs";
import { merge } from "lodash";
import { add, number } from "mathjs";
import { create } from "zustand";
import { devtools, persist } from "zustand/middleware";
import type { ItemListId } from "../../tracking/create-ecommerce-event";
import { type CartProduct, cartProductSchema, type Product } from "../models";
import { addToCartEvent, removeFromCartEvent, safeTrackEvent } from "../tracking/events";

export type CartStore = {
  products: CartProduct[];
  expireTime: string;
  getTotalPrice: () => number;
  getStandardProduct: () => CartProduct | undefined;
  setProducts: (products: CartProduct[]) => void;
  addProduct: (product: Product, itemListId?: ItemListId, index?: number) => void;
  removeProduct: (cartProduct: CartProduct | Product) => void;
  toggleProduct: (product: Product, itemListId?: ItemListId, index?: number) => void;
  reset: () => void;
};

type DefaultState = Pick<CartStore, "products" | "expireTime">;

const LS_EXPIRE_HOURS = 48;

const defaultState: DefaultState = {
  products: [],
  expireTime: dayjs().add(LS_EXPIRE_HOURS, "hour").toISOString(),
};

export const useCartStore = create<CartStore>()(
  devtools(
    persist(
      (set, get) => ({
        ...defaultState,
        getTotalPrice: () => get().products.reduce((acc, product) => add(acc, number(product.grossPrice)), 0),
        getStandardProduct: () => get().products.find((p) => !p.isUpsell),
        setProducts: (products: CartProduct[]) => set({ products }),
        addProduct: (product: Product, itemListId?: ItemListId, index?: number) => {
          const cartProduct: CartProduct = {
            ...(cartProductSchema.cast(product) as CartProduct),
            item_list_id: itemListId,
            index,
          };

          const fireAddToCart = () =>
            safeTrackEvent(
              () =>
                addToCartEvent(
                  [
                    {
                      ...product,
                      productType: "blood-test-package" as const,
                      quantity: 1,
                      item_list_id: itemListId,
                      index,
                    },
                  ],
                  { value: Number(product.grossPrice) },
                ),
              "add_to_cart",
              product.isUpsell ? "cart-store addProduct upsell" : "cart-store addProduct standard",
            );

          const state = get();

          if (!product.isUpsell) {
            // Standard package replaces the entire cart. Anything currently in the cart
            // (the previous standard pick + any upsell add-ons) gets dropped, so emit a
            // remove_from_cart for each so the datalayer reflects the full state change,
            // not just the new addition. Skip the new product itself if it was already present
            // (re-selecting the same package is a no-op for removal).
            const productsBeingRemoved = state.products.filter((p) => p.uuid !== product.uuid);
            for (const removed of productsBeingRemoved) {
              safeTrackEvent(
                () =>
                  removeFromCartEvent([{ ...removed, productType: "blood-test-package" as const }], {
                    value: Number(removed.grossPrice) * removed.quantity,
                  }),
                "remove_from_cart",
                "cart-store addProduct standard replace",
              );
            }
            fireAddToCart();
            set({ products: [cartProduct] });
            return;
          }

          const alreadyInCart = state.products.some((p) => p.uuid === product.uuid);

          if (!alreadyInCart) {
            fireAddToCart();
            set({ products: [...state.products, cartProduct] });
          }
        },
        removeProduct: (cartProduct: CartProduct | Product) => {
          const state = get();
          const existing = state.products.find((p) => p.uuid === cartProduct.uuid);

          if (!existing) return;

          safeTrackEvent(
            () =>
              removeFromCartEvent([{ ...existing, productType: "blood-test-package" as const }], {
                value: Number(existing.grossPrice) * existing.quantity,
              }),
            "remove_from_cart",
            "cart-store removeProduct",
          );

          set({
            products: state.products.filter((p) => p.uuid !== cartProduct.uuid),
          });
        },
        toggleProduct: (product: Product, itemListId?: ItemListId, index?: number) => {
          const isProductInCart = get().products.some((p) => p.uuid === product.uuid);

          if (isProductInCart) {
            get().removeProduct(product);
          } else {
            get().addProduct(product, itemListId, index);
          }
        },
        reset: () => {
          set(() => ({
            products: [],
          }));
          useCartStore.persist.clearStorage();
        },
      }),
      {
        name: "OrderingGroupActionsCartStore",
        merge: (pState, cState) => {
          if (!pState) return cState;
          const persistedState = pState as DefaultState;

          const now = dayjs();
          const expireTimeDate = dayjs(persistedState.expireTime);
          const diffHours = now.diff(expireTimeDate, "hour");

          if (diffHours >= LS_EXPIRE_HOURS) useCartStore.persist.clearStorage();

          return merge(cState, persistedState);
        },
      },
    ),
  ),
);
